/**
 * SF-T-268: what each sub-task of a one-at-a-time parent waits on once the sub-tasks form a graph (any names another),
 * read the same way by the pick-up (worker runs/one-at-a-time.ts) and the list's drawing (frontend TaskSubtasks).
 *
 * One that names sub-tasks waits on those (in the list; the server refuses a loop among them). One that names none
 * waits on the nearest one before it in `order` that doesn't already wait on it, directly or through others: so the
 * list's order never makes a loop with what's named, however the names and the order were set (the picker, the API,
 * sf-agent, a drag). With none such, it waits on nothing. Taken in `order`, each one's wait is chosen against those
 * chosen before it, so the whole is never a loop.
 */

export interface SubtaskGraphNode {
  id: string;
  /** The sub-tasks (ids) it names. */
  after: readonly string[];
}

export interface SubtaskWaits {
  after: string[];
  /** Not named: the one before it in the order. */
  implicit: boolean;
}

/** `order`: the chain's sub-tasks in the list's order (ids; the picked ones when there's a pick, never drafts). */
export function subtaskWaits(nodes: readonly SubtaskGraphNode[], order: readonly string[]): Map<string, SubtaskWaits> {
  const ids = new Set(nodes.map((n) => n.id));
  const waits = new Map<string, SubtaskWaits>(
    nodes.map((n) => [n.id, { after: [...new Set(n.after)].filter((id) => ids.has(id) && id !== n.id), implicit: false }])
  );
  const reaches = (from: string, to: string): boolean => {
    const seen = new Set<string>();
    const stack = [from];
    while (stack.length) {
      const id = stack.pop()!;
      if (id === to) return true;
      if (seen.has(id)) continue;
      seen.add(id);
      stack.push(...(waits.get(id)?.after ?? []));
    }
    return false;
  };
  const inOrder = order.filter((id) => ids.has(id));
  inOrder.forEach((id, at) => {
    const own = waits.get(id)!;
    if (own.after.length) return;
    for (let i = at - 1; i >= 0; i--) {
      if (reaches(inOrder[i], id)) continue;
      waits.set(id, { after: [inOrder[i]], implicit: true });
      return;
    }
  });
  return waits;
}
